import { Plus, Settings, HelpCircle, LogOut, RotateCcw, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useAuth } from "@/hooks/useAuth";
import { useEnterpriseCapabilities } from "@/hooks/useEnterpriseCapabilities";
import { WorkspaceOnboarding } from "@/components/workspace/WorkspaceOnboarding";
import { useTour } from "@/components/onboarding/TourProvider";
import {
  FOUNDATIONAL_TOUR_ID,
  FOUNDATIONAL_TOUR_VERSION,
  buildFoundationalTour,
} from "@/lib/tours/foundational-v2";
import { DontShowAgainCheckbox } from "@/components/onboarding/DontShowAgainCheckbox";

export function WorkspaceNav() {
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspaces();
  const { signOut } = useAuth();
  const { hasAnyEnterpriseCapability } = useEnterpriseCapabilities();
  const { startTour } = useTour();
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleRestartTour = () => {
    const finalContent = (
      <div>
        You&apos;re set. Want a refresher? Restart the tour anytime from this
        menu.
        <DontShowAgainCheckbox />
      </div>
    );
    startTour(
      FOUNDATIONAL_TOUR_ID,
      FOUNDATIONAL_TOUR_VERSION,
      buildFoundationalTour(finalContent),
    );
  };

  const getWorkspaceInitials = (name?: string) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!currentWorkspace) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 px-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10">
                {getWorkspaceInitials(currentWorkspace.name)}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium hidden sm:inline-block">
              {currentWorkspace.name}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          {/* Workspaces — cap the visible list to ~5 rows and scroll the rest, so the
              static actions below (New Workspace, Settings, Log out, …) always stay in view. */}
          <div className="px-2 py-2 max-h-72 overflow-y-auto">
            {workspaces.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => switchWorkspace(workspace.id)}
              >
                <div className="flex items-center w-full py-1">
                  <Avatar className="h-8 w-8 mr-2">
                    <AvatarFallback className="bg-primary/10">
                      {getWorkspaceInitials(workspace.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{workspace.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {workspace.sso_owner_label
                        ? workspace.sso_owner_label
                        : `${workspace.member_count || 1} members · ${workspace.contact_count || 0} contacts`}
                    </p>
                  </div>
                  {currentWorkspace.id === workspace.id && (
                    <span className="flex h-2 w-2 rounded-full bg-primary ml-2"></span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>

          <DropdownMenuSeparator />

          {/* Primary Actions */}
          <DropdownMenuItem 
            className="px-2 py-1.5"
            onClick={() => setShowOnboarding(true)}
          >
            <div className="flex items-center">
              <Plus className="mr-2 h-4 w-4" />
              <span>New Workspace</span>
            </div>
          </DropdownMenuItem>

          {/* M4 (#569): gated entry into the unified /enterprise shell.
              Self-hides for non-enterprise users (hook returns false). */}
          {hasAnyEnterpriseCapability && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/enterprise" className="px-2 py-1.5">
                  <Building2 className="mr-2 h-4 w-4" />
                  Enterprise
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />

          {/* Settings & Support */}
          <DropdownMenuItem asChild>
            <Link to="/settings" className="px-2 py-1.5">
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link to="/help" className="px-2 py-1.5">
              <HelpCircle className="mr-2 h-4 w-4" />
              Help & Support
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            data-tour-id="help.restart-tour"
            onClick={handleRestartTour}
            className="px-2 py-1.5"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Restart Tour
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Logout */}
          <DropdownMenuItem 
            onClick={signOut} 
            className="text-red-600 px-2 py-1.5"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <WorkspaceOnboarding 
        open={showOnboarding} 
        onOpenChange={setShowOnboarding} 
      />
    </>
  );
}